import { useState, useEffect, useCallback } from "react";
import { Spinner, Flex } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";
import { Manager } from "../../../Manager";
import type { RawHeader, RawRow } from "../../../../lib/import-db";
import type { SearchParams, SortParams } from "../../../Manager/types";
import {
  fetchRawData,
  fetchDistinctRawValues,
  fetchRawHeaders,
} from "../../../../state/raw-data";

export function RawDataTab() {
  const { t } = useTranslation();
  const [headers, setHeaders] = useState<RawHeader[] | null>(null);

  useEffect(() => {
    fetchRawHeaders().then(setHeaders);
  }, []);

  const fetchData = useCallback(
    async (
      limit?: number,
      offset?: number,
      searchParams?: SearchParams,
      sortParams?: SortParams,
    ) => {
      return fetchRawData(limit, offset, searchParams, sortParams);
    },
    [],
  );

  const fetchDistinct = useCallback(
    async (
      filterKey: string,
      otherFilters?: Record<string, unknown>,
    ): Promise<{ value: string | number; label: string }[]> => {
      return fetchDistinctRawValues(filterKey, otherFilters);
    },
    [],
  );

  if (!headers) {
    return (
      <Flex align="center" justify="center" p="6">
        <Spinner size="3" />
      </Flex>
    );
  }

  return (
    <Manager.Root<RawRow>
      title=""
      fetchData={fetchData}
      limit={100}
      allowSelection={false}
    >
      <Manager.Search>
        <Manager.DynamicSearch.Root fetchDistinctValues={fetchDistinct}>
          {headers.map((h) => (
            <Manager.DynamicSearch.Filter
              key={h.key}
              filterKey={h.key}
              label={`${h.key} — ${h.label}`}
            />
          ))}
        </Manager.DynamicSearch.Root>
      </Manager.Search>

      <Manager.Table>
        <Manager.Column<RawRow>
          header={t("import.lineNumber")}
          width="80px"
          accessor={(row) => row.lineNumber}
        />
        {headers.map((h) => (
          <Manager.Column<RawRow>
            key={h.key}
            header={h.label}
            accessor={(row) => String(row[h.key] ?? "")}
          />
        ))}
      </Manager.Table>
    </Manager.Root>
  );
}
